import { useEffect, useMemo, useState } from "react"

import { App, Checkbox, Flex, Form, Skeleton, Space, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import PrivacyPolicy from "../../../../../Components/PrivacyPolicy"
import SelectComponent from "../../../../../Components/Select"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { modalConfig } from "../../../../../theme"
import utils from "../../../../../Utils/utils"
import {
  validateArnCode,
  validateBinCode,
  validateCaidCode
} from "../../../ConfigureProfilePage/utils/codeValidators"
import {
  clearActivationError,
  selectActivation,
  selectActivationDescriptors
} from "../../redux/business.slice"
import { activate, getActivationDescriptors } from "../../redux/business.thunks"

import styles from "./activationModal.module.less"

const { Title, Text, Link } = Typography

// Matches BillingDescriptorsFormConfigure's tooltip icon.
const TOOLTIP_ICON_STYLE = { fontSize: 16, color: "#7A7A83" }

// The chip the onboarding Alerts form renders under its descriptor input
// (Components/Forms/BillingDescriptorsForm). Copied rather than imported: there
// it is a private component inside a 25KB legacy form, and exporting it would
// mean editing that file for presentation alone.
const FilterTag = ({ tag, closable, onDelete, theme }) => (
  <Tag
    bordered={theme === "dark"}
    color="processing"
    closable={closable}
    onClose={onDelete}
    closeIcon={
      <Icon type="cross_small" style={{ fontSize: 20, lineHeight: 1, color: "#8aa7f4" }} />
    }
    style={{
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 6,
      padding: "0px 4px 0px 10px",
      margin: 0,
      textWrap: "balance",
      display: "flex",
      alignItems: "center",
      color: "#1448CB",
      background: theme === "dark" ? "none" : "#F2F6FF"
    }}
  >
    {tag}
  </Tag>
)

// Receipt activation flow (BLOCK 5 of CHA1-1967). Full-screen modal with
// billing-descriptor tags plus, for Order Insight, either 4 ARN codes or a
// BIN+CAID pair. Continue POSTs to /business/activation/ - success closes the
// modal and refetches programs + descriptors (§14).
export const ActivationModal = ({ program, onClose, businessAccountId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { modal } = App.useApp()
  const isMobile = useSelector((s) => !!s.nav?.isMobile)
  const theme = useSelector((s) => s.nav?.theme)
  const { saving, error: activationError } = useSelector(selectActivation)
  const { rows: existingDescriptors, fetching: descriptorsFetching } = useSelector(
    selectActivationDescriptors
  )

  const isOI = program?.program === "oi"

  // Existing descriptors are locked (non-deletable) chips - the merchant may
  // only add new ones through this flow, never remove one that already exists.
  const locked = useMemo(
    () => (existingDescriptors || []).map((d) => d.billing_descriptor).filter(Boolean),
    [existingDescriptors]
  )
  const lockedSet = useMemo(() => new Set(locked.map((s) => s.toUpperCase())), [locked])

  const [tags, setTags] = useState([])
  const [useBinCaid, setUseBinCaid] = useState(false)
  const [arn, setArn] = useState([])
  const [bin, setBin] = useState([])
  const [caid, setCaid] = useState([])
  const [errors, setErrors] = useState({})

  // Redux-owned activation error persists between mounts — clear it so the
  // freshly-opened modal never shows a stale message from the previous attempt.
  // Descriptors are fetched here (not derived from the page-level list) so the
  // locked chips reflect ALL existing descriptors, not the filtered view.
  useEffect(() => {
    dispatch(clearActivationError())
    if (program?.program)
      dispatch(getActivationDescriptors({ program: program.program, businessAccountId }))
  }, [dispatch, program?.program, businessAccountId])

  // Sync locked chips into tags once they load — the async fetch resolves
  // after mount, so a lazy initializer wouldn't see them.
  useEffect(() => {
    setTags((prev) => {
      const kept = new Set(prev.filter((v) => !lockedSet.has(String(v).toUpperCase())))
      for (const l of locked) kept.add(l)
      return [...kept]
    })
  }, [locked, lockedSet])

  if (!program) return null

  const newTags = tags.filter((tag) => !lockedSet.has(tag.toUpperCase()))
  const dirty = newTags.length > 0 || arn.length > 0 || bin.length > 0 || caid.length > 0

  const handleTagsChange = (value) => {
    // Keep locked tags no matter what the Select emits - Antd removes on
    // backspace even when we style the × away, so guard it here too.
    const kept = new Set(value.map((s) => s.trim()).filter(Boolean))
    for (const l of locked) kept.add(l)
    setTags([...kept])
  }

  // Removal now happens on the chip below the field, not inside the input.
  const handleRemoveTag = (tag) => {
    if (lockedSet.has(String(tag).toUpperCase())) return
    setTags((prev) => prev.filter((value) => value !== tag))
  }

  // Every field here hides its own tags, so each renders its values as chips
  // underneath instead.
  const renderChips = (values, setValues) =>
    values.length ? (
      <Flex gap={8} wrap="wrap" className={styles.chips}>
        {values.map((value) => (
          <FilterTag
            key={value}
            tag={value}
            theme={theme}
            closable
            onDelete={() => setValues(values.filter((item) => item !== value))}
          />
        ))}
      </Flex>
    ) : null

  const validateTags = () => {
    if (newTags.length === 0 && locked.length === 0) {
      return t("This field is required.")
    }
    return null
  }

  const validateArnList = () => {
    if (!arn.length) return t("This field is required.")
    if (arn.length !== 4) return t("Enter exactly 4 different ARN codes.")
    if (new Set(arn).size !== 4) return t("ARN codes must be different.")
    for (const c of arn) {
      const err = validateArnCode(c)
      if (err) return t(err)
    }
    return null
  }

  const validateBinList = () => {
    if (!bin.length) return t("This field is required.")
    for (const c of bin) {
      const err = validateBinCode(c)
      if (err) return t(err)
    }
    return null
  }

  const validateCaidList = () => {
    if (!caid.length) return t("This field is required.")
    for (const c of caid) {
      const err = validateCaidCode(c)
      if (err) return t(err)
    }
    return null
  }

  const handleContinue = async () => {
    const next = {}
    const tErr = validateTags()
    if (tErr) next.tags = tErr

    if (isOI) {
      if (useBinCaid) {
        const bErr = validateBinList()
        if (bErr) next.bin = bErr
        const cErr = validateCaidList()
        if (cErr) next.caid = cErr
      } else {
        const aErr = validateArnList()
        if (aErr) next.arn = aErr
      }
    }

    setErrors(next)
    if (Object.keys(next).length) return

    // Send the full descriptor set (locked + new), not just newTags — the
    // backend requires a non-empty list, and a merchant on OI who only added
    // ARN/BIN/CAID would otherwise submit an empty descriptors array.
    const payload = { program: program.program, billing_descriptors: tags }
    if (isOI) {
      if (useBinCaid) {
        payload.submission_mode = "BIN_CAID"
        payload.bin_codes = bin
        payload.caid_codes = caid
      } else {
        payload.submission_mode = "ARN"
        payload.arn_codes = arn
      }
    }

    const result = await dispatch(activate({ ...payload, businessAccountId }))
    if (result.meta.requestStatus === "fulfilled") onClose()
  }

  // The platform's shared confirm dialog (theme.js modalConfig) — the same one
  // onboarding uses when leaving a half-filled step (CHA1-2301).
  const handleClose = () => {
    if (!dirty) {
      onClose()
      return
    }

    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: t("Are you sure you want to exit?"),
      content: t("Any changes you have made will not be saved."),

      cancelText: t("Stay"),
      okText: t("Exit"),
      onOk() {
        onClose()
      }
    })
  }

  const arnLabel = t("4 different VISA ARN codes (for Visa transactions)")
  const arnPlaceholder = t("Separate VISA ARN codes with commas")
  const binPlaceholder = t("Separate VISA BIN codes with commas")
  const caidPlaceholder = t("Separate VISA CAID codes with commas")

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      data-testid="deflect-activation-modal"
    >
      {/* Same header the onboarding steps use (Components/LoginHeader): the
          themed logo centred, close button pinned right. */}
      <div className={styles.header}>
        <img
          src={Images[theme === "dark" ? "logoWithTextWhite" : "logoWithText"]}
          alt="Chargeback"
        />
        <ButtonComponent
          size="large"
          icon={<Icon type="close_modal" />}
          style={{ position: "absolute", right: 32 }}
          onClick={handleClose}
          className="flex center"
          data-testid="deflect-activation-close-btn"
        />
      </div>

      <div className={styles.body}>
        <div className={styles.content}>
          {/* Title left, "Learn more" right — the same header row as the
              onboarding Alerts step (OnboardingAlertsPage). */}
          <Flex
            gap={16}
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
            wrap="wrap"
          >
            <Space direction="vertical" size={0}>
              <Title level={5} style={{ textAlign: "left" }}>
                {t("Receipt activation")}
              </Title>
              <Text type="secondary">
                {t("To activate Receipt, submit your billing descriptor.")}
              </Text>
            </Space>

            <Flex gap={12}>
              <ButtonComponent
                className="flex center small-space"
                block
                onClick={() =>
                  utils.goLink(
                    "https://help.chargeback.io/en/collections/16516790-setting-up-enrollments",
                    "_blank"
                  )
                }
                style={{ padding: "4px 20px" }}
              >
                {t("Learn more")}
                <Icon type="edit" style={{ fontSize: 20, color: "#7A7A83" }} />
              </ButtonComponent>
            </Flex>
          </Flex>

          {/* The form keeps the width it has on the onboarding step (523px), so
              widening the page to 1040 spaces out the header row without
              stretching the inputs across it. */}
          {/* antd Form purely for presentation: it supplies the required mark,
              the tooltip icon and the error slot exactly as
              BillingDescriptorsFormConfigure does. Values stay in local state,
              so no Form.Item carries a `name`. */}
          <Form
            className={cls("with-default-error", styles.formPane)}
            layout="vertical"
            requiredMark
          >
            <Form.Item
              label={t("Billing descriptor")}
              required
              tooltip={{
                title: t(
                  "The billing descriptor is the text you set in your payment processor. It appears on your customer's statement to identify each purchase. For merchants using Shopify, the Chargeback platform automatically adds the SP prefix to the billing descriptor."
                ),
                icon: (
                  <div>
                    <Icon type="circle_info" style={TOOLTIP_ICON_STYLE} />
                  </div>
                ),
                overlayStyle: { minWidth: 248 }
              }}
              validateStatus={errors.tags ? "error" : ""}
              help={errors.tags || null}
            >
              {descriptorsFetching ? (
                <Skeleton.Input active block size="large" />
              ) : (
                <>
                  {/* Reads as a plain text input rather than a dropdown
                    (CHA1-2301): no arrow, the menu never opens, and chips are
                    rendered below instead of inside. */}
                  <SelectComponent
                    mode="tags"
                    size="large"
                    value={tags}
                    open={false}
                    suffixIcon={null}
                    tagRender={() => null}
                    tokenSeparators={[","]}
                    placeholder={t("Separate descriptor with commas")}
                    onChange={handleTagsChange}
                    className={cls("hidden-tags like-input", {
                      "has-value_descriptor": !!tags.length
                    })}
                    data-testid="deflect-activation-descriptor-select"
                  />
                  {tags.length ? (
                    <Flex gap={8} wrap="wrap" className={styles.chips}>
                      {tags.map((tag) => (
                        <FilterTag
                          key={tag}
                          tag={tag}
                          theme={theme}
                          closable={!lockedSet.has(tag.toUpperCase())}
                          onDelete={() => handleRemoveTag(tag)}
                        />
                      ))}
                    </Flex>
                  ) : null}
                </>
              )}
            </Form.Item>

            {isOI ? (
              <>
                {!useBinCaid ? (
                  <Form.Item
                    label={arnLabel}
                    required
                    tooltip={{
                      title: t(
                        "ARN codes are required for RDR enrollment. Enter at least 4 ARN codes per descriptor you wish to enroll for RDR. Only ARN codes starting with 2 or 7 are valid."
                      ),
                      icon: (
                        <div>
                          <Icon type="circle_info" style={TOOLTIP_ICON_STYLE} />
                        </div>
                      ),
                      overlayStyle: { minWidth: 248 }
                    }}
                    validateStatus={errors.arn ? "error" : ""}
                    help={errors.arn || null}
                  >
                    <SelectComponent
                      mode="tags"
                      size="large"
                      value={arn}
                      open={false}
                      suffixIcon={null}
                      tagRender={() => null}
                      tokenSeparators={[","]}
                      placeholder={arnPlaceholder}
                      onChange={setArn}
                      disabled={descriptorsFetching}
                      className={cls("hidden-tags like-input", {
                        "has-value_arn": !!arn.length
                      })}
                      data-testid="deflect-activation-arn-select"
                    />
                    {renderChips(arn, setArn)}
                  </Form.Item>
                ) : null}

                {/* Boxed, like the configure form's BIN/CAID opt-in. */}
                <Form.Item style={{ border: "1px solid #E1E1E2", borderRadius: 8, padding: 12 }}>
                  <Checkbox
                    checked={useBinCaid}
                    onChange={(e) => setUseBinCaid(e.target.checked)}
                    disabled={descriptorsFetching}
                    data-testid="deflect-activation-bin-caid-checkbox"
                  >
                    <Text strong>{t("I want to use BIN and CAID instead of ARNs.")}</Text>
                  </Checkbox>
                  <Text
                    type="secondary"
                    strong
                    style={{ display: "block", marginLeft: 26, marginTop: 4 }}
                  >
                    {t("Select this if you don't have any ARN codes.")}
                    <Link
                      href="https://help.chargeback.io/en/articles/12782785-how-to-get-bins-caid-for-rdr-alerts"
                      target="_blank"
                    >
                      {" "}
                      {t("Learn how to get your BIN and CAID")}
                    </Link>
                  </Text>
                </Form.Item>

                {useBinCaid ? (
                  <>
                    <Form.Item
                      label={t("BIN code")}
                      required
                      validateStatus={errors.bin ? "error" : ""}
                      help={errors.bin || null}
                    >
                      <SelectComponent
                        mode="tags"
                        size="large"
                        value={bin}
                        open={false}
                        suffixIcon={null}
                        tagRender={() => null}
                        tokenSeparators={[","]}
                        placeholder={binPlaceholder}
                        onChange={setBin}
                        disabled={descriptorsFetching}
                        className="hidden-tags like-input"
                      />
                      {renderChips(bin, setBin)}
                    </Form.Item>
                    <Form.Item
                      label={t("CAID code")}
                      required
                      validateStatus={errors.caid ? "error" : ""}
                      help={errors.caid || null}
                    >
                      <SelectComponent
                        mode="tags"
                        size="large"
                        value={caid}
                        open={false}
                        suffixIcon={null}
                        tagRender={() => null}
                        tokenSeparators={[","]}
                        placeholder={caidPlaceholder}
                        onChange={(vals) => setCaid(vals.map((v) => v.toUpperCase()))}
                        disabled={descriptorsFetching}
                        className="hidden-tags like-input"
                      />
                      {renderChips(caid, setCaid)}
                    </Form.Item>
                  </>
                ) : null}
              </>
            ) : null}

            {activationError ? <div className={styles.error}>{activationError}</div> : null}
          </Form>

          {/* Directly under the form, 320px wide and centred — as in
              OnboardingAlertsPage. */}
          <Flex justify="center" style={{ width: "100%" }}>
            <ButtonComponent
              type="primary"
              size="large"
              loading={saving}
              disabled={descriptorsFetching}
              onClick={handleContinue}
              className="flex center"
              style={{ width: 320, marginTop: "auto" }}
              data-testid="deflect-activation-continue-btn"
            >
              {t("Continue")}
            </ButtonComponent>
          </Flex>
        </div>
      </div>

      <div className={styles.footer}>
        <PrivacyPolicy />
      </div>
    </div>
  )
}
